import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useIntro } from "@/context/IntroContext";

const EASE = [0.22, 1, 0.36, 1];
const TRAVEL_EASE = [0.76, 0, 0.24, 1];

const CSS = `
@keyframes nh-open {
  0%   { -webkit-mask-position: 95% 0; mask-position: 95% 0; opacity: 0; }
  8%   { opacity: 1; }
  100% { -webkit-mask-position: 5% 0; mask-position: 5% 0; opacity: 1; }
}
@keyframes nh-shine-sweep {
  0%   { background-position: 175% 0; opacity: 0; }
  14%  { opacity: 1; }
  86%  { opacity: 1; }
  100% { background-position: -85% 0; opacity: 0; }
}

.nh-logo-wrap { position: relative; }
/* smooth soft-edged opening wipe — the diagonal edge sweeps left→right so the N appears,
   then the H, with no hard line or cut */
.nh-mark {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: contain; display: block; user-select: none; -webkit-user-drag: none;
  opacity: 0;
  -webkit-mask-image: linear-gradient(103deg, #000 0%, #000 50%, rgba(0,0,0,0) 80%, rgba(0,0,0,0) 100%);
          mask-image: linear-gradient(103deg, #000 0%, #000 50%, rgba(0,0,0,0) 80%, rgba(0,0,0,0) 100%);
  -webkit-mask-size: 235% 100%; mask-size: 235% 100%;
  -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
  -webkit-mask-position: 120% 0; mask-position: 120% 0;
  animation: nh-open 2.7s cubic-bezier(0.32,0.02,0.16,1) 0.6s forwards;
}
.nh-shine {
  position: absolute; inset: 0; pointer-events: none;
  -webkit-mask: url('/splash-mark.png') center / contain no-repeat;
          mask: url('/splash-mark.png') center / contain no-repeat;
  background-image: linear-gradient(102deg, rgba(255,255,255,0) 42%, rgba(255,247,222,0.9) 50%, rgba(255,255,255,0) 58%);
  background-repeat: no-repeat;
  background-size: 220% 100%;
  background-position: 175% 0;
  opacity: 0;
  animation: nh-shine-sweep 1.8s cubic-bezier(0.4,0,0.2,1) 3.6s 1 both;
}

.nh-static .nh-mark { opacity: 1; -webkit-mask: none; mask: none; animation: none; }
.nh-static .nh-shine { animation: none; opacity: 0; }
`;

export default function Splash() {
  const { endIntro } = useIntro();
  const [show, setShow] = useState(true);
  const [travel, setTravel] = useState(null); // {x,y,scale}
  const [bgOut, setBgOut] = useState(false);
  const [logoOut, setLogoOut] = useState(false);
  const [light, setLight] = useState(false);
  const [wordIn, setWordIn] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.body.style.overflow = "hidden";
    const timers = [];

    const finish = () => { endIntro(); };
    const remove = () => { setShow(false); document.body.style.overflow = ""; };

    const computeTarget = () => {
      const wrap = logoRef.current;
      const mark = document.querySelector('[data-testid="nav-logo-mark"]')
                || document.querySelector('[data-testid="nav-logo"]');
      if (!wrap || !mark) return null;
      const w = wrap.getBoundingClientRect();
      const n = mark.getBoundingClientRect();
      if (!n.width || !w.width) return null;
      // splash monogram fills its image (content ~95.9% tall, centred at 0.499/0.499)
      const MARK_FRAC = 0.959, MARK_CX = 0.499, MARK_CY = 0.499;
      // the nav corner logo (logo-nh.png) has padding: its visible glyph is only 53.3%
      // of the box, centred at x 0.517 / y 0.450 — match that so the sizes are identical
      const NAV_FRAC = 0.533, NAV_CX = 0.517, NAV_CY = 0.450;
      const scale = (n.height * NAV_FRAC) / (w.height * MARK_FRAC);
      const c0x = w.left + w.width / 2, c0y = w.top + w.height / 2;
      const gx = c0x + (MARK_CX - 0.5) * w.width;   // splash glyph centre (wrap coords)
      const gy = c0y + (MARK_CY - 0.5) * w.height;
      const navx = n.left + n.width * NAV_CX;        // nav glyph centre (screen)
      const navy = n.top + n.height * NAV_CY;
      // translate so the scaled glyph centre lands on the nav glyph centre
      const x = navx - c0x - scale * (gx - c0x);
      const y = navy - c0y - scale * (gy - c0y);
      return { x, y, scale };
    };

    if (reduced) {
      timers.push(setTimeout(finish, 500));
      timers.push(setTimeout(remove, 900));
    } else {
      timers.push(setTimeout(() => setLight(true), 300));   // warm light arrives
      timers.push(setTimeout(() => setWordIn(true), 2500));  // wordmark fades up (own transition)
      // travel into the navbar after the emboss + light travel + wordmark have played
      timers.push(setTimeout(() => {
        const t = computeTarget();
        if (t) { setTravel(t); setBgOut(true); }
        else { finish(); remove(); }
      }, 6300));
      // once fully arrived (travel ~1.1s), crossfade with the real navbar logo
      timers.push(setTimeout(() => { setLogoOut(true); finish(); }, 7500));
      timers.push(setTimeout(remove, 8150));
    }

    return () => { timers.forEach(clearTimeout); document.body.style.overflow = ""; };
  }, [endIntro]);

  const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="splash-screen"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: "transparent", pointerEvents: "none" }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <style>{CSS}</style>

          {/* sunlit plaster wall — current colored background; fades out to reveal the site during travel */}
          <motion.div
            className="absolute inset-0"
            style={{ backgroundColor: "#c4ad90", backgroundImage: "url('/splash-wall.jpg')", backgroundSize: "cover", backgroundPosition: "center" }}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: bgOut ? 0 : 1, scale: bgOut ? 1.02 : 1 }}
            transition={{ duration: bgOut ? 0.9 : 1.2, ease: EASE }}
          />

          {/* warm daylight wash that arrives over the scene */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              mixBlendMode: "screen",
              backgroundImage:
                "linear-gradient(135deg, rgba(255,244,214,0.55) 0%, rgba(255,240,205,0.18) 34%, rgba(255,235,195,0) 60%)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: bgOut ? 0 : (light ? 1 : 0) }}
            transition={{ duration: bgOut ? 0.8 : 1.6, ease: EASE }}
          />

          {/* the one logo element that embosses in, holds, then travels into the navbar */}
          <motion.div
            className={`relative ${reduced ? "nh-static" : ""}`}
            animate={{
              x: travel ? travel.x : 0,
              y: travel ? travel.y : 0,
              scale: travel ? travel.scale : 1,
              opacity: logoOut ? 0 : 1,
            }}
            transition={{
              x: { duration: 1.2, ease: EASE },
              y: { duration: 1.2, ease: EASE },
              scale: { duration: 1.2, ease: EASE },
              opacity: { duration: 0.55, ease: "easeInOut" },
            }}
            style={{ transformOrigin: "center center" }}
          >
            <div ref={logoRef} className="nh-logo-wrap" style={{ width: "min(32vw, 300px)", aspectRatio: "969 / 800" }}>
              <img className="nh-mark" src="/splash-mark.png" alt="Nat Habitat monogram" draggable="false" />
              <div className="nh-shine" aria-hidden="true" />
            </div>
          </motion.div>

          {/* wordmark — its own gentle fade-up transition, fades out as the monogram travels */}
          <motion.div
            className="relative"
            style={{ width: "min(31vw, 290px)", marginTop: "min(3.4vw, 32px)" }}
            initial={{ opacity: 0, y: 16 }}
            animate={reduced ? { opacity: 1, y: 0 } : (bgOut ? { opacity: 0, y: 6 } : (wordIn ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }))}
            transition={{ duration: bgOut ? 0.5 : 1.2, ease: EASE }}
          >
            <img src="/splash-word.png" alt="Nat Habitat — Interior Design Studio" className="block w-full h-auto" draggable="false" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
